<template>
  <div class="data-management well-page qaqc-overview-page">
    <QAQCOverviewShimmer v-if="loading" />
    <UnifiedQAQCDashboard
      v-else-if="mappedDashboard"
      :summary-stats="summaryStats"
      :qc-status-overview="qcStatusOverview"
      :qc-status-panel="qcStatusPanel"
      :active-test-tab="activeTestTab"
      :scorecard-rows="scorecardRows"
      :scorecard-summary="scorecardSummary"
      :validation-matrix-rows="validationMatrixRows"
      :validation-matrix-title="validationMatrixTitle"
      :validation-matrix-description="validationMatrixDescription"
      :distribution-plot-options="distributionPlotOptions"
      :selected-plot-id="selectedPlotId"
      :distribution-data="distributionData"
      :sample-ids="sampleIds"
      :selected-sample-ids="selectedSampleIds"
      :consistency-check-rows="consistencyCheckRows"
      @update:active-test-tab="activeTestTab = $event"
      @update:selected-plot-id="selectedPlotId = $event"
      @update:selected-sample-ids="selectedSampleIds = $event"
    />
    <div v-else-if="!loading && error" class="tabs-no-test-div timeout">
      <div class="text-center">
        {{ error }}
        <v-icon flat @click="fetchOverviewData()" color="blue-darken-3" icon="mdi-reload" size="large"></v-icon>
      </div>
    </div>
  </div>
</template>

<script>
import UnifiedQAQCDashboard from "@/components/layout/QAQC/Overview/UnifiedQAQCDashboard.vue";
import QAQCOverviewShimmer from "@/components/layout/QAQC/Overview/QAQCOverviewShimmer.vue";
import { fetchQaqcDashboard, mapDashboardToOverview } from "./qaqcDashboardApi.js";

function pct(total, part) {
  if (!total || total === 0) return "0%";
  return `${Math.round((Number(part) / Number(total)) * 100)}%`;
}

function distPlotToData(plot, selectedId) {
  if (!plot || (plot.Name !== selectedId && plot.DisplayName !== selectedId)) return null;
  const keys = plot.DistributionPlotKeys || [];
  const categories = keys.map((k) => k.Key);
  const values = keys.map((k) => Number(k.Value) || 0);
  const total = values.reduce((a, b) => a + b, 0);
  const validKeys = keys.filter((k) => k.Key !== "N/A" && k.Key !== "Failed" && k.Key !== "Not Reported");
  const validSum = validKeys.reduce((a, k) => a + (Number(k.Value) || 0), 0);
  const outsideSum = total - validSum;
  return {
    categories,
    validRange: {
      values,
      label: "Valid Range",
      count: total === 0 ? "-" : validSum,
      percent: total ? pct(total, validSum) : "-",
    },
    outsideRange: {
      count: total === 0 ? "-" : outsideSum,
      label: total === 0 ? "Outside Range - not available" : (outsideSum ? `Outside Range (${outsideSum} samples)` : "Outside Range"),
    },
  };
}

export default {
  name: "QAQCOverview",
  components: {
    UnifiedQAQCDashboard,
    QAQCOverviewShimmer,
  },
  data() {
    return {
      loading: false,
      error: null,
      mappedDashboard: null,
      activeTestTab: "oilCce",
      selectedPlotId: "",
      selectedSampleIds: [],
    };
  },
  computed: {
    summaryStats() {
      return this.mappedDashboard?.summaryStats || this.defaultSummaryStats();
    },
    qcStatusOverview() {
      return this.mappedDashboard?.qcStatusOverview || this.defaultQcStatusOverview();
    },
    qcStatusPanel() {
      return this.mappedDashboard?.qcStatusPanel || this.defaultQcStatusPanel();
    },
    tabData() {
      const byTab = this.mappedDashboard?.byTab || {};
      return byTab[this.activeTestTab] || {};
    },
    scorecardRows() {
      return this.tabData.scorecardRows || [];
    },
    scorecardSummary() {
      return this.tabData.scorecardSummary || this.defaultScorecardSummary();
    },
    validationMatrixRows() {
      return this.tabData.validationMatrixRows || [];
    },
    validationMatrixTitle() {
      return this.tabData.validationMatrixTitle || "Validation Pass Rate Matrix";
    },
    validationMatrixDescription() {
      return this.tabData.validationMatrixDescription || "Percentage of all samples passing each validation check";
    },
    consistencyCheckRows() {
      return this.tabData.consistencyCheckRows || [];
    },
    distributionPlotOptions() {
      return this.tabData.distributionPlotOptions || [];
    },
    distributionData() {
      const plots = this.tabData.distributionPlots || [];
      const firstId = this.distributionPlotOptions[0]?.id;
      const plotId = this.selectedPlotId || firstId;
      if (!plotId || !plots.length) return this.defaultDistributionData();
      const plot = plots.find((p) => p.Name === plotId);
      const data = plot ? distPlotToData(plot, plotId) : null;
      return data || this.defaultDistributionData();
    },
    sampleIds() {
      return this.mappedDashboard?.sampleIds || [];
    },
  },
  watch: {
    distributionPlotOptions: {
      immediate: true,
      handler(opts) {
        if (opts.length) {
          const hasCurrent = opts.some((o) => o.id === this.selectedPlotId);
          if (!hasCurrent || !this.selectedPlotId) this.selectedPlotId = opts[0].id;
        }
      },
    },
    activeTestTab() {
      const opts = this.distributionPlotOptions;
      if (opts.length) this.selectedPlotId = opts[0].id;
    },
  },
  mounted() {
    this.fetchOverviewData();
  },
  methods: {
    defaultSummaryStats() {
      return {
        totalTests: "-",
        totalTestsChange: "",
        cceTests: "-",
        cceTestsPercent: "",
        dlTests: "-",
        dlTestsPercent: "",
        passRate: "-",
      };
    },
    defaultQcStatusOverview() {
      return {
        categories: ["Passed", "Failed", "Needs Review"],
        series: [
          { name: "CCE Tests", color: "#2298D5", values: [0, 0, 0] },
          { name: "DL Tests", color: "#F97316", values: [0, 0, 0] },
        ],
      };
    },
    defaultQcStatusPanel() {
      return {
        passed: { count: "-", total: "-", percent: "-" },
        needsReview: { count: "-", total: "-", percent: "-" },
        failed: { count: "-", total: "-", percent: "-" },
        subtitle: "Current QC validation results",
      };
    },
    defaultScorecardSummary() {
      return {
        overallPassRate: "-",
        passedQc: "-",
        pendingQc: "-",
        failed: "-",
      };
    },
    defaultDistributionData() {
      return {
        categories: [],
        validRange: { values: [], label: "Valid Range", count: "-", percent: "-" },
        outsideRange: { count: "-", label: "Outside Range - not available" },
      };
    },
    fetchOverviewData() {
      this.loading = true;
      this.error = null;
      fetchQaqcDashboard()
        .then((data) => {
          this.mappedDashboard = mapDashboardToOverview(data);
        })
        .catch((err) => {
          this.error = err?.message || "Failed to load QA/QC dashboard";
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
@import "../../assets/Styling/qaqc.css";
</style>
